import { auth, storage } from '../lib/firebase';
import { deleteObject, getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { uploadImage } from './imageUpload';
import { compressImageFile } from '../utils/imageCompress';

function safeFilename(name: string) {
  return (name || 'upload')
    .replace(/[^\w.\-]+/g, '_')
    .replace(/_+/g, '_')
    .slice(0, 120);
}

export type MarketplaceImageUploadResult = {
  url: string;
  path: string;
};

/**
 * Upload a marketplace image to a listing-owned path.
 * Path: marketplace/{sellerId}/{listingId}/{slot}_{timestamp}_{safeName}.jpg
 *
 * The listing id in the path lets seller/admin/account cleanup remove an entire
 * listing image tree without guessing which timestamped files belong to it.
 */
export async function uploadMarketplaceImage(
  file: File,
  listingId: string,
  slot = 0,
): Promise<MarketplaceImageUploadResult> {
  const user = auth.currentUser;
  if (!user) throw new Error('Must be signed in.');

  const cleanListingId = String(listingId || '').trim().replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 120);
  if (!cleanListingId) throw new Error('Listing id is required for marketplace images.');
  if (!file?.type?.startsWith('image/')) throw new Error('Choose an image file.');
  if (file.size > 20 * 1024 * 1024) throw new Error('Choose an image smaller than 20 MB.');

  const safeName = safeFilename(file.name).replace(/\.[^.]+$/, '') || 'image';
  const path = `marketplace/${user.uid}/${cleanListingId}/${Math.max(0, Math.floor(slot))}_${Date.now()}_${safeName}.jpg`;

  const url = await uploadImage(file, path, {
    maxSizeMB: 1.5,
    maxDim: 1600,
    contentType: 'image/jpeg',
  });

  return { url, path };
}

export async function deleteMarketplaceImages(paths: Array<string | null | undefined>): Promise<void> {
  const user = auth.currentUser;
  if (!user) throw new Error('Must be signed in.');
  const prefix = `marketplace/${user.uid}/`;
  const unique = Array.from(new Set((paths || []).map((p) => String(p || '').trim()).filter(Boolean)));

  await Promise.all(
    unique.map(async (path) => {
      if (!path.startsWith(prefix)) return;
      await deleteIfPresent(path);
    }),
  );
}

export type TournamentCoverUploadResult = {
  coverImageUrl: string;
  coverThumbUrl: string;
  coverImagePath: string;
  coverImageUpdatedAt: number;
};

export type CrewCoverUploadResult = {
  coverImageUrl: string;
  coverThumbUrl: string;
  coverImagePath: string;
  coverImageUpdatedAt: number;
};

const deleteIfPresent = async (path: string) => {
  try {
    await deleteObject(ref(storage, path));
  } catch (error: any) {
    if (error?.code !== 'storage/object-not-found') throw error;
  }
};

/**
 * Upload one optional public-facing tournament cover and a lightweight card thumb.
 * Deterministic paths mean replacing a cover does not leave orphaned uploads.
 *
 * Paths:
 * tournamentCovers/{uid}/{tournamentId}/cover.jpg
 * tournamentCovers/{uid}/{tournamentId}/thumb.jpg
 */
export async function uploadTournamentCover(
  tournamentId: string,
  file: File,
): Promise<TournamentCoverUploadResult> {
  const user = auth.currentUser;
  if (!user) throw new Error('Must be signed in.');
  if (!tournamentId) throw new Error('Tournament id is required.');
  if (!file.type.startsWith('image/')) throw new Error('Choose an image file.');
  if (file.size > 20 * 1024 * 1024) {
    throw new Error('Choose an image smaller than 20 MB.');
  }

  let heroBlob = await compressImageFile(file, {
    maxDim: 1280,
    quality: 0.78,
    mimeType: 'image/jpeg',
  });
  if (heroBlob.size > 700 * 1024) {
    heroBlob = await compressImageFile(
      new File([heroBlob], 'cover.jpg', { type: 'image/jpeg' }),
      { maxDim: 1080, quality: 0.68, mimeType: 'image/jpeg' },
    );
  }

  let thumbBlob = await compressImageFile(file, {
    maxDim: 640,
    quality: 0.72,
    mimeType: 'image/jpeg',
  });
  if (thumbBlob.size > 240 * 1024) {
    thumbBlob = await compressImageFile(
      new File([thumbBlob], 'thumb.jpg', { type: 'image/jpeg' }),
      { maxDim: 520, quality: 0.62, mimeType: 'image/jpeg' },
    );
  }

  const basePath = `tournamentCovers/${user.uid}/${tournamentId}`;
  const heroPath = `${basePath}/cover.jpg`;
  const thumbPath = `${basePath}/thumb.jpg`;
  const metadata = { contentType: 'image/jpeg', cacheControl: 'public,max-age=3600' };

  const [heroSnapshot, thumbSnapshot] = await Promise.all([
    uploadBytes(ref(storage, heroPath), heroBlob, metadata),
    uploadBytes(ref(storage, thumbPath), thumbBlob, metadata),
  ]);

  const [heroUrl, thumbUrl] = await Promise.all([
    getDownloadURL(heroSnapshot.ref),
    getDownloadURL(thumbSnapshot.ref),
  ]);
  const coverImageUpdatedAt = Date.now();
  const cacheBust = (url: string) => `${url}${url.includes('?') ? '&' : '?'}v=${coverImageUpdatedAt}`;

  return {
    coverImageUrl: cacheBust(heroUrl),
    coverThumbUrl: cacheBust(thumbUrl),
    coverImagePath: basePath,
    coverImageUpdatedAt,
  };
}

export async function deleteTournamentCover(tournamentId: string): Promise<void> {
  const user = auth.currentUser;
  if (!user) throw new Error('Must be signed in.');
  if (!tournamentId) throw new Error('Tournament id is required.');

  const basePath = `tournamentCovers/${user.uid}/${tournamentId}`;
  await Promise.all([
    deleteIfPresent(`${basePath}/cover.jpg`),
    deleteIfPresent(`${basePath}/thumb.jpg`),
  ]);
}

/**
 * Upload one private-crew presentation cover plus a lightweight Dock thumb.
 * Deterministic paths prevent orphaned replacements.
 *
 * Paths:
 * crewCovers/{uid}/{crewId}/cover.jpg
 * crewCovers/{uid}/{crewId}/thumb.jpg
 */
export async function uploadCrewCover(
  crewId: string,
  file: File,
): Promise<CrewCoverUploadResult> {
  const user = auth.currentUser;
  if (!user) throw new Error('Must be signed in.');
  if (!crewId) throw new Error('Crew id is required.');
  if (!file.type.startsWith('image/')) throw new Error('Choose an image file.');
  if (file.size > 20 * 1024 * 1024) {
    throw new Error('Choose an image smaller than 20 MB.');
  }

  let heroBlob = await compressImageFile(file, {
    maxDim: 1280,
    quality: 0.78,
    mimeType: 'image/jpeg',
  });
  if (heroBlob.size > 700 * 1024) {
    heroBlob = await compressImageFile(
      new File([heroBlob], 'cover.jpg', { type: 'image/jpeg' }),
      { maxDim: 1080, quality: 0.68, mimeType: 'image/jpeg' },
    );
  }

  let thumbBlob = await compressImageFile(file, {
    maxDim: 640,
    quality: 0.72,
    mimeType: 'image/jpeg',
  });
  if (thumbBlob.size > 240 * 1024) {
    thumbBlob = await compressImageFile(
      new File([thumbBlob], 'thumb.jpg', { type: 'image/jpeg' }),
      { maxDim: 520, quality: 0.62, mimeType: 'image/jpeg' },
    );
  }

  const basePath = `crewCovers/${user.uid}/${crewId}`;
  const heroPath = `${basePath}/cover.jpg`;
  const thumbPath = `${basePath}/thumb.jpg`;
  const metadata = { contentType: 'image/jpeg', cacheControl: 'public,max-age=3600' };

  const [heroSnapshot, thumbSnapshot] = await Promise.all([
    uploadBytes(ref(storage, heroPath), heroBlob, metadata),
    uploadBytes(ref(storage, thumbPath), thumbBlob, metadata),
  ]);

  const [heroUrl, thumbUrl] = await Promise.all([
    getDownloadURL(heroSnapshot.ref),
    getDownloadURL(thumbSnapshot.ref),
  ]);
  const coverImageUpdatedAt = Date.now();
  const cacheBust = (url: string) => `${url}${url.includes('?') ? '&' : '?'}v=${coverImageUpdatedAt}`;

  return {
    coverImageUrl: cacheBust(heroUrl),
    coverThumbUrl: cacheBust(thumbUrl),
    coverImagePath: basePath,
    coverImageUpdatedAt,
  };
}

export async function deleteCrewCover(crewId: string): Promise<void> {
  const user = auth.currentUser;
  if (!user) throw new Error('Must be signed in.');
  if (!crewId) throw new Error('Crew id is required.');

  const basePath = `crewCovers/${user.uid}/${crewId}`;
  await Promise.all([
    deleteIfPresent(`${basePath}/cover.jpg`),
    deleteIfPresent(`${basePath}/thumb.jpg`),
  ]);
}
